import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { personalityAPI } from '@/services/api';
import { speakHybrid, stopSpeaking } from '@/services/voiceService';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Globe, Zap, Check, Crown, Sun, Moon, Monitor, Loader2, BarChart3, Volume2, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getErrorMessage } from '@/utils/errorHandler';
import PushNotificationSettings from '@/components/PushNotificationSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Premium avatar images for Lira
const LIRA_AVATARS = {
  female: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=face',
  male: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face'
};

// Voice preview sample texts
const VOICE_SAMPLES = {
  female: "Hi! I'm Lira, your conversation companion. Let's practice together!",
  male: "Hi! I'm Lira, your conversation companion. Let's practice together!"
};

const SettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const { setTheme } = useTheme();
  const [personality, setPersonality] = useState(null);
  const [targetLanguage, setTargetLanguage] = useState('English');
  const [level, setLevel] = useState('beginner');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [planStatus, setPlanStatus] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [playingPreview, setPlayingPreview] = useState(null); // 'female' | 'male' | null
  const [themeMode, setThemeMode] = useState(() => {
    const saved = localStorage.getItem('speakflow-theme-mode');
    return saved || 'system';
  });

  useEffect(() => {
    fetchPersonality();
    fetchPlanStatus();
    fetchSubscriptionStatus();
    if (user) {
      setTargetLanguage(user.target_language || user.learning_language || 'English');
      setLevel(user.level || 'beginner');
    }
    
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    }
  }, [user, searchParams]);

  const fetchPlanStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/plan/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlanStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch plan status');
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscriptions/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar? Você manterá acesso até o fim do período pago.')) {
      return;
    }
    
    setCancellingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/subscriptions/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchSubscriptionStatus();
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/subscriptions/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchSubscriptionStatus();
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCancellingSubscription(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const MAX_ATTEMPTS = 15;
    
    if (attempts >= MAX_ATTEMPTS) {
      toast.error('Aguardando confirmação. Pode levar alguns minutos.');
      setCheckingPayment(false);
      return;
    }
    
    setCheckingPayment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.upgraded) {
        toast.success('Pagamento confirmado! Você agora é Premium!');
        await refreshUser();
        await fetchPlanStatus();
        window.history.replaceState({}, '', '/settings');
        setCheckingPayment(false);
      } else if (response.data.waiting_webhook) {
        toast.info('Pagamento recebido! Aguardando confirmação...');
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API}/subscriptions/checkout`,
        { origin_url: originUrl },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 20000
        }
      );
      
      if (response.data.url) {
        toast.success('Redirecionando para pagamento...');
        window.location.href = response.data.url;
      }
    } catch (error) {
      setUpgrading(false);
      toast.error(getErrorMessage(error));
    }
  };

  const fetchPersonality = async () => {
    try {
      const data = await personalityAPI.get();
      setPersonality(data);
    } catch (error) {
      // Non-critical
    } finally {
      setLoading(false);
    }
  };

  const handleUserUpdate = async (field, value) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (field === 'target_language') {
        await axios.put(
          `${API}/auth/update-language?target_language=${value}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTargetLanguage(value);
      } else if (field === 'level') {
        await axios.put(
          `${API}/auth/complete-onboarding`,
          { learning_language: targetLanguage, level: value, goal: user?.goal || 'travel' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setLevel(value);
      }
      await refreshUser();
      toast.success('Salvo!');
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleVoiceChange = async (gender) => {
    setSaving(true);
    try {
      await personalityAPI.update({ voice_gender: gender });
      setPersonality(prev => ({ ...prev, voice_gender: gender }));
      
      // Store in localStorage for immediate global access
      localStorage.setItem('lira_voice_gender', gender);
      
      // Dispatch custom event so other components can react
      window.dispatchEvent(new CustomEvent('liraVoiceChanged', { detail: { gender } }));
      
      // Show clear confirmation
      toast.success(
        gender === 'female' ? '✓ Voz Feminina ativada' : '✓ Voz Masculina ativada',
        { description: 'A Lira agora usará esta voz em toda a plataforma.' }
      );
    } catch (error) {
      toast.error('Erro ao salvar voz');
    } finally {
      setSaving(false);
    }
  };

  // Preview voice sample
  const handlePreviewVoice = async (gender) => {
    // Stop any playing audio first
    if (playingPreview) {
      stopSpeaking();
      setPlayingPreview(null);
      return;
    }
    
    setPlayingPreview(gender);
    
    try {
      await speakHybrid(VOICE_SAMPLES[gender], {
        gender: gender,
        speed: 1.0,
        onStart: () => {},
        onEnd: () => setPlayingPreview(null),
        onError: () => {
          setPlayingPreview(null);
          toast.error('Erro ao reproduzir amostra');
        }
      });
    } catch (error) {
      setPlayingPreview(null);
      toast.error('Erro ao reproduzir amostra');
    }
  };

  const handleThemeChange = (mode) => {
    setThemeMode(mode);
    localStorage.setItem('speakflow-theme-mode', mode);
    
    if (mode === 'system') {
      localStorage.removeItem('speakflow-theme');
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(systemDark ? 'dark' : 'light');
    } else {
      setTheme(mode);
    }
    toast.success(`Tema ${mode === 'light' ? 'claro' : mode === 'dark' ? 'escuro' : 'do sistema'} ativado`);
  };

  // Language names in Portuguese
  const languageLabels = {
    'English': 'Inglês',
    'Spanish': 'Espanhol',
    'French': 'Francês',
    'German': 'Alemão',
    'Chinese': 'Chinês'
  };

  const levelLabels = {
    'beginner': 'Iniciante',
    'intermediate': 'Intermediário',
    'advanced': 'Avançado'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-forest/20 border-t-forest rounded-full animate-spin mx-auto mb-3" />
          <p className="text-forest text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" data-testid="settings-page">
      {/* Header */}
      <header className="bg-white border-b border-forest/10 p-4">
        <div className="flex items-center gap-4 max-w-lg mx-auto">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-fraunces text-xl font-semibold text-forest">Configurações</h1>
        </div>
      </header>

      <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
        
        {/* Plan Card - Simplified */}
        <div className={`rounded-2xl p-5 shadow-soft ${planStatus?.is_premium ? 'bg-gradient-to-r from-amber-50 to-amber-100/50 border border-amber-200' : 'bg-white'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${planStatus?.is_premium ? 'bg-amber-200' : 'bg-blue-100'}`}>
                {planStatus?.is_premium ? <Crown className="w-5 h-5 text-amber-600" /> : <Zap className="w-5 h-5 text-blue-600" />}
              </div>
              <div>
                <h3 className="font-semibold text-forest text-sm">
                  {planStatus?.is_premium ? 'Plano Premium' : 'Plano Gratuito'}
                </h3>
                <p className="text-xs text-stone">
                  {planStatus?.is_premium 
                    ? 'Conversas ilimitadas' 
                    : `${planStatus?.messages_remaining || 0} mensagens restantes`}
                </p>
              </div>
            </div>
            {!planStatus?.is_premium && (
              <Button
                onClick={handleUpgrade}
                disabled={upgrading}
                size="sm"
                className="bg-forest hover:bg-forest/90 text-sand rounded-full text-xs px-4"
                data-testid="upgrade-btn"
              >
                {upgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'R$14,90/mês'}
              </Button>
            )}
          </div>
          
          {/* Subscription Management */}
          {planStatus?.is_premium && subscriptionStatus?.has_subscription && (
            <div className="mt-4 pt-4 border-t border-amber-200/50">
              {subscriptionStatus.cancel_at_period_end ? (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-orange-600">
                    Acesso até {new Date(subscriptionStatus.current_period_end).toLocaleDateString('pt-BR')}
                  </p>
                  <Button
                    onClick={handleReactivateSubscription}
                    disabled={cancellingSubscription}
                    variant="outline"
                    size="sm"
                    className="text-xs text-green-600 border-green-600"
                  >
                    {cancellingSubscription ? '...' : 'Reativar'}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-stone">
                    Renova em {new Date(subscriptionStatus.current_period_end).toLocaleDateString('pt-BR')}
                  </p>
                  <Button
                    onClick={handleCancelSubscription}
                    disabled={cancellingSubscription}
                    variant="ghost"
                    size="sm"
                    className="text-xs text-red-500"
                  >
                    {cancellingSubscription ? '...' : 'Cancelar'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Learning Settings - Simplified */}
        <div className="bg-white rounded-2xl p-5 shadow-soft">
          <h2 className="font-semibold text-forest text-sm mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Configurações de Aprendizado
          </h2>
          
          <div className="space-y-4">
            {/* Language */}
            <div>
              <Label className="text-forest text-xs mb-2 block">Idioma que está aprendendo</Label>
              <Select value={targetLanguage} onValueChange={(v) => handleUserUpdate('target_language', v)}>
                <SelectTrigger className="rounded-xl h-11 bg-sand border-0">
                  <SelectValue>{languageLabels[targetLanguage] || targetLanguage}</SelectValue>
                </SelectTrigger>
                <SelectContent className="z-[9999] bg-white rounded-xl shadow-lg border p-1">
                  {Object.entries(languageLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value} className="rounded-lg px-3 py-2 cursor-pointer hover:bg-sand">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Level */}
            <div>
              <Label className="text-forest text-xs mb-2 block">Seu nível</Label>
              <Select value={level} onValueChange={(v) => handleUserUpdate('level', v)}>
                <SelectTrigger className="rounded-xl h-11 bg-sand border-0">
                  <SelectValue>{levelLabels[level] || level}</SelectValue>
                </SelectTrigger>
                <SelectContent className="z-[9999] bg-white rounded-xl shadow-lg border p-1">
                  {Object.entries(levelLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value} className="rounded-lg px-3 py-2 cursor-pointer hover:bg-sand">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Lira Voice/Appearance Settings */}
        <div className="bg-white rounded-2xl p-5 shadow-soft">
          <h2 className="font-semibold text-forest text-sm mb-2 flex items-center gap-2">
            <Volume2 className="w-4 h-4" />
            Voz da Lira
          </h2>
          <p className="text-xs text-stone mb-4">
            Escolha como a Lira vai soar em toda a plataforma
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Female Voice Option */}
            <div className="space-y-3">
              <button
                onClick={() => handleVoiceChange('female')}
                disabled={saving}
                className={`w-full p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-3 ${
                  (personality?.voice_gender || 'female') === 'female' 
                    ? 'border-lime bg-lime/10 shadow-md' 
                    : 'border-transparent bg-sand hover:bg-sand/70'
                }`}
                data-testid="voice-female-option"
              >
                <div className="relative">
                  <img 
                    src={LIRA_AVATARS.female}
                    alt="Lira - Voz Feminina"
                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                  />
                  {(personality?.voice_gender || 'female') === 'female' && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-lime rounded-full flex items-center justify-center border-2 border-white">
                      <Check className="w-3 h-3 text-forest" />
                    </div>
                  )}
                </div>
                <span className={`text-sm ${(personality?.voice_gender || 'female') === 'female' ? 'text-forest font-semibold' : 'text-stone'}`}>
                  Voz Feminina
                </span>
              </button>
              
              {/* Preview Button */}
              <button
                onClick={() => handlePreviewVoice('female')}
                className={`w-full py-2.5 px-4 rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-all ${
                  playingPreview === 'female'
                    ? 'bg-forest text-white'
                    : 'bg-sand text-forest hover:bg-forest/10'
                }`}
                data-testid="preview-female-btn"
              >
                {playingPreview === 'female' ? (
                  <>
                    <Pause className="w-3.5 h-3.5" />
                    Reproduzindo...
                  </>
                ) : (
                  <>
                    <Play className="w-3.5 h-3.5" />
                    Ouvir amostra
                  </>
                )}
              </button>
            </div>
            
            {/* Male Voice Option */}
            <div className="space-y-3">
              <button
                onClick={() => handleVoiceChange('male')}
                disabled={saving}
                className={`w-full p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-3 ${
                  personality?.voice_gender === 'male' 
                    ? 'border-lime bg-lime/10 shadow-md' 
                    : 'border-transparent bg-sand hover:bg-sand/70'
                }`}
                data-testid="voice-male-option"
              >
                <div className="relative">
                  <img 
                    src={LIRA_AVATARS.male}
                    alt="Lira - Voz Masculina"
                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                  />
                  {personality?.voice_gender === 'male' && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-lime rounded-full flex items-center justify-center border-2 border-white">
                      <Check className="w-3 h-3 text-forest" />
                    </div>
                  )}
                </div>
                <span className={`text-sm ${personality?.voice_gender === 'male' ? 'text-forest font-semibold' : 'text-stone'}`}>
                  Voz Masculina
                </span>
              </button>
              
              {/* Preview Button */}
              <button
                onClick={() => handlePreviewVoice('male')}
                className={`w-full py-2.5 px-4 rounded-lg text-xs font-medium flex items-center justify-center gap-2 transition-all ${
                  playingPreview === 'male'
                    ? 'bg-forest text-white'
                    : 'bg-sand text-forest hover:bg-forest/10'
                }`}
                data-testid="preview-male-btn"
              >
                {playingPreview === 'male' ? (
                  <>
                    <Pause className="w-3.5 h-3.5" />
                    Reproduzindo...
                  </>
                ) : (
                  <>
                    <Play className="w-3.5 h-3.5" />
                    Ouvir amostra
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-lime/10 rounded-xl border border-lime/20">
            <p className="text-xs text-forest text-center">
              A Lira sempre será sua companheira de conversação. A voz escolhida será aplicada em conversas, lições, listening e todos os áudios.
            </p>
          </div>
        </div>

        {/* Theme Settings */}
        <div className="bg-white rounded-2xl p-5 shadow-soft">
          <h2 className="font-semibold text-forest text-sm mb-4">Aparência</h2>
          
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'light', icon: Sun, label: 'Claro' },
              { value: 'dark', icon: Moon, label: 'Escuro' },
              { value: 'system', icon: Monitor, label: 'Auto' },
            ].map((option) => {
              const Icon = option.icon;
              const isSelected = themeMode === option.value;
              
              return (
                <button
                  key={option.value}
                  onClick={() => handleThemeChange(option.value)}
                  className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${
                    isSelected ? 'border-lime bg-lime/10' : 'border-transparent bg-sand hover:bg-sand/70'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isSelected ? 'text-forest' : 'text-stone'}`} />
                  <span className={`text-xs ${isSelected ? 'text-forest font-medium' : 'text-stone'}`}>
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Push Notification Settings */}
        <PushNotificationSettings user={user} />

        {/* Legal Links */}
        <div className="pt-4">
          <div className="flex justify-center gap-6 text-xs text-stone">
            <a href="/terms" className="hover:text-forest">Termos de Uso</a>
            <a href="/privacy" className="hover:text-forest">Privacidade</a>
            {/* Admin Only - Analytics Link */}
            {user?.role === 'admin' && (
              <button 
                onClick={() => navigate('/admin/dashboard')} 
                className="hover:text-forest flex items-center gap-1"
              >
                <BarChart3 className="w-3 h-3" />
                Admin
              </button>
            )}
          </div>
        </div>

        {saving && (
          <div className="text-center text-stone text-xs py-2">Salvando...</div>
        )}
        
        {checkingPayment && (
          <div className="text-center text-blue-600 text-xs py-2">Verificando pagamento...</div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
